import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/components/Button";
import Icon from "../common/components/Icon";
import useClientConfig from "../common/hooks/clientConfigHook";
import { useJobRequiredComment } from "../hooks/jobRequiredCommentHook";
import useNetwork from "../common/hooks/networkHook";
import { readField } from "../common/utils/objectUtilities";
import useCredentials from "../common/hooks/credentialsHook";
import { removeAssistance, useJobAssistance } from "../reducers/jobAssistanceSlice";
import { continueJob, createJob, jobIsValid, resetNewJob, updateJob, useJob } from "../reducers/jobSlice";
import { useLevel } from "../reducers/levelSlice";
import { useProblem } from "../reducers/problemSlice";
import { useSite } from "../reducers/siteSlice";
import { useType } from "../reducers/typeSlice";
import PropTypes from 'prop-types';
import { useSelectedJobid } from "../utils/CADStateProvider";
import { DefaultButton } from "../common/components/DefaultButton";
import JobContinueButton from "./JobContinueButton";
import useMessageDisplay from "../common/components/MessageDisplay";
import { ReactNodeDialog } from "../common/utils/dialogUtlities";
import DateInput from "../common/components/DateInput";
import TimeInput from "../common/components/TimeInput";
import Select from "../common/components/Select";
import ReactForm from "../common/components/ReactForm";
import { Col, Row } from "../common/components/Grid";
import { now } from "../common/utils/CDate";
import { useSpecializedTranslation } from "../hooks/specializedTranslationHook";

const JobRescheduleDialog = ({ show, onClose }) => {
    const { t } = useTranslation();
    const [interval, setInterval] = useState();
    const [date, setDate] = useState();
    const formRef = useRef();

    const handleSave = useCallback(() => {
        if (formRef.current?.checkValidity()) {
            if (interval !== 'other') {
                onClose({ reopentime: now().add(interval, 'hours').toISOString() });
            } else {
                onClose({ reopentime: date });
            }
        }
    }, [date, interval, onClose]);

    const options = useMemo(() => [
        { label: t('hour', { count: 1 }), value: 1 },
        { label: t('hour', { count: 2 }), value: 2 },
        { label: t('hour', { count: 4 }), value: 4 },
        { label: t('hour', { count: 6 }), value: 6 },
        { label: t('hour', { count: 8 }), value: 8 },
        { label: t('hour', { count: 12 }), value: 12 },
        { label: t('hour', { count: 24 }), value: 24 },
        { label: t('hour', { count: 36 }), value: 36 },
        { label: t('hour', { count: 48 }), value: 48 },
        { label: t('hour', { count: 72 }), value: 72 },
        { label: t('other'), value: 'other' }
    ], [t]);

    return <ReactNodeDialog showModal={show} title={t('rescheduleJob')} onClose={() => onClose(false)} onSave={handleSave}>
        <ReactForm ref={formRef}>
            <Row>
                <Col>
                    <Select value={interval} placeholder={t('reschedule')} onChange={setInterval} options={options} labelField="label" valueField="value" required></Select>
                </Col>
            </Row>
            { interval === 'other' && <Row>
                <Col md>
                    <DateInput placeholder={t('reopenDate')} value={date} onChange={setDate} required></DateInput>
                </Col>
                <Col md>
                    <TimeInput placeholder={t('reopenTime')} value={date} onChange={setDate} required></TimeInput>
                </Col>
            </Row> }
        </ReactForm>
    </ReactNodeDialog>
}

const JobControlButtons = ({jobid, disabled = false, onJobEnd, onJobCreate }) => {
    const credentials = useCredentials();
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const { showError } = useMessageDisplay();
    const { setSelectedJobId } = useSelectedJobid();
    const [pendingReschedule, setPendingReschedule] = useState(false);
    const { processRequiredComment, requiredCommentModal } = useJobRequiredComment();

    const job = useJob(jobid);
    const assistance = useJobAssistance(job.jobid);
    const { t } = useSpecializedTranslation(job);

    const site = useSite(job.site);
    const level = useLevel(job.site, job.level);
    const type = useType(job.site, job.level, job.type);
    const problem = useProblem(job.site, job.level, job.type, job.problem);

    const processing = useRef(false);

    const requiredFields = useMemo(() => {
        let requiredFields = [];
        if (clientConfig.jobGeneralFormSpec) {
            requiredFields = requiredFields.concat(clientConfig.jobGeneralFormSpec.map(section => section.rows.flat()).flat().filter(cell => cell.required).map(cell => 'generalcustomfields.' + cell.field));
        }
        if (site?.formdef && Array.isArray(site.formdef)) {
            requiredFields = requiredFields.concat(site?.formdef.map(section => section.rows.flat()).flat().filter(cell => cell.required).map(cell => 'customfields.' + cell.field));
        }
        if (level?.formdef && Array.isArray(level.formdef)) {
            requiredFields = requiredFields.concat(level?.formdef.map(section => section.rows.flat()).flat().filter(cell => cell.required).map(cell => 'customfields.' + cell.field));
        }
        if (type?.formdef && Array.isArray(type.formdef)) {
            requiredFields = requiredFields.concat(type?.formdef.map(section => section.rows.flat()).flat().filter(cell => cell.required).map(cell => 'customfields.' + cell.field));
        }
        if (problem?.formdef && Array.isArray(problem.formdef)) {
            requiredFields = requiredFields.concat(problem?.formdef.map(section => section.rows.flat()).flat().filter(cell => cell.required).map(cell => 'customfields.' + cell.field));
        }
        if (clientConfig.locationMode === 'address') {
            requiredFields = requiredFields.concat(['customfields.__location_details__.address1', 'customfields.__location_details__.suburb', 'customfields.__location_details__.state', 'customfields.__location_details__.postcode'])
            if (clientConfig.address?.number) {
                requiredFields.push('customfields.__location_details__.number');
            }
        }
        return requiredFields;
    }, [clientConfig, site?.formdef, level?.formdef, type?.formdef, problem?.formdef])
    const isValid = jobIsValid(clientConfig, job, requiredFields)

    const hasFull = useMemo(() => credentials.access.hasFull({ site: job.site, level: job.level, type: job.type }), [credentials, job])
    const canClose = useMemo(() => {
        const hasFullAssistance = credentials.access.hasFull(assistance.map(a => ({ type: a.type, level: a.level, site: a.site })));
        const hasLog = credentials.access.hasLog({ site: job.site, level: job.level, type: job.type }) || credentials.access.hasLog(assistance.map(a => ({ type: a.type, level: a.level, site: a.site })));

        return hasFull || (clientConfig.assistanceJobClose && hasFullAssistance) || (clientConfig.logOnlyJobClose && hasLog);
    }, [credentials, job, assistance, clientConfig.assistanceJobClose, clientConfig.logOnlyJobClose, hasFull]);
    
    const hasFullAssistance = useMemo(() => {
        return credentials.access.hasFull(assistance.map(a => ({ type: a.type, level: a.level, site: a.site })));
    }, [credentials, assistance]);

    useEffect(() => {
        if (!clientConfig.enableJobCreateButton && job.jobid === undefined && isValid && !processing.current && onJobCreate) {
            processing.current = true;
            processRequiredComment(job, 'New', createJob, { ...job, origin: 'Chronicler' }, async result => { onJobCreate((await result)?.jobid) });
        }
    }, [clientConfig, job, onJobCreate, processRequiredComment, isValid]);

    useEffect(() => {
        processing.current = false;
    }, [job.jobid]);

    const endJob = (operation, delta) => {
        if (canClose) {
            // check required custom fields all have values
            const getRequiredFields = obj => obj?.customfields?.flatMap(section => section.rows.flat(2).filter(cell => cell.required).map(cell => ({ title: cell.title, field: cell.field }))) ?? [];
            const requiredFields = getRequiredFields(site?.formdef).concat(getRequiredFields(level?.formdef)).concat(getRequiredFields(type?.formdef)).concat(getRequiredFields(problem?.formdef));
            let errors = requiredFields.filter(entry => readField(job.customfields, entry.field) == null).map(entry => t('missingRequiredField', { name: entry.title }));

            // now process disallowed field values per their rules
            errors = errors.concat(Object.entries(clientConfig.disallowedFieldValues?.job).map(([field, prohibitedValues]) => {
                // if we have a non-array then we need to find a matching option to get the list of prohibited values
                if (!Array.isArray(prohibitedValues)) {
                    prohibitedValues = prohibitedValues.options.find(opt => Object.keys(opt).filter(f => f !== 'prohibitedValues').every(f => readField(job, f) == opt[f]))?.prohibitedValues
                }
                // check the field against the prohibited values
                const toCheck = readField(job, field);
                if (prohibitedValues && prohibitedValues.some(value => {
                    if (typeof toCheck === 'string' && typeof value === 'string') {
                        return toCheck.toLowerCase() === value.toLowerCase();
                    } else if (toCheck === value || (value == null && toCheck == value) || (Array.isArray(toCheck) && Array.isArray(value) && toCheck.some(e => value.some(v => v === e)))) {
                        return true;
                    }
                    return false;
                })) {
                    return t('disallowedFieldValue', { name: field.substring(field.lastIndexOf('.')+1) });
                }
                return null;
            }).filter(err => err));

            // check if we need a result
            if (clientConfig.enableResultPriority >= job.priority && !job.result) {
                errors.push(t('missingRequiredField', { name: 'Result' }));
            }
            if (errors.length > 0) {
                showError(<ul>{errors.map(e => <li>{e}</li>)}</ul>)
                return;
            }

            // handle required comment
            if (!processing.current) {
                processing.current = true;
                processRequiredComment(job, 'End', operation, delta, () => { onJobEnd(); processing.current = false; });
            }
        } else if (hasFullAssistance) {
            assistance.filter(a => credentials.access.hasFull(a)).forEach(a => removeAssistance(clientConfig, network, a));
        }
    }

    const handleCreate = () => {
        if (!processing.current) {
            processing.current = true;
            processRequiredComment(job, 'New', createJob, { ...job, origin: 'Chronicler' }, async result => { onJobCreate((await result)?.jobid); processing.current = false; });
        }
    }

    const handleReopen = () => {
        if (!processing.current) {
            processing.current = true;
            processRequiredComment(job, 'Re-open', updateJob, { jobid: job.jobid, status: 'Re-opened' }, () => { processing.current = false});
        }
    }

    const handleReschedule = result => {
        if (result) {
            updateJob(network, { jobid, status: 'Closed', reopentime: result.reopentime });
            setPendingReschedule(false);
            setSelectedJobId();
        } else {
            setPendingReschedule(false);
        }
    }

    const enableEnd = (canClose || hasFullAssistance) && jobid && !job.locked && job.status !== 'Closed';
    const enableReopen = (canClose || hasFull) && jobid && !job.locked && job.status === 'Closed';
    const enableCreate = !jobid && clientConfig.enableJobCreateButton;

    return <>
        <JobRescheduleDialog show={pendingReschedule} onClose={handleReschedule}></JobRescheduleDialog>
        { requiredCommentModal() }
        { !jobid ? <DefaultButton kind="clear" layout="stacked" onClick={() => resetNewJob(network)}></DefaultButton> : null }
        { enableCreate && onJobCreate ? <Button variant="primary" layout="stacked" disabled={disabled || !isValid} onClick={() => handleCreate()}><Icon intent="add"></Icon> {t('create')}</Button> : null }
        { enableReopen ? <Button variant="default" layout="stacked" disabled={disabled || !isValid} onClick={() => handleReopen()}><Icon variant="regular" kind="folder-open"></Icon> {t('reopen')}</Button> : null }
        { enableEnd && clientConfig.enableJobReschedule ? <Button variant="default" layout="stacked" disabled={disabled || !isValid} onClick={() => setPendingReschedule(true)}><Icon variant="solid" kind="clock-rotate-left"></Icon> {t('reschedule')}</Button> : null }
        { enableEnd && onJobEnd && clientConfig.enableContinuation ? <JobContinueButton disabled={disabled || !isValid} onContinue={newdetails => endJob(continueJob, { newdetails, update: { jobid: jobid, status: 'Closed' } })}></JobContinueButton> : null }
        { enableEnd && onJobEnd ? <Button variant="danger" layout="stacked" disabled={disabled || !isValid} onClick={() => endJob(updateJob, { jobid, status: 'Closed' })}><Icon variant="regular" kind="circle-stop"></Icon> {t('end')}</Button> : null }
    </>
}

JobControlButtons.propTypes = {
    jobid: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    disabled: PropTypes.bool,
    onJobEnd: PropTypes.func,
    onJobCreate: PropTypes.func
}

export default JobControlButtons;
